import React, { useEffect, useState } from 'react';
import { AlertTriangle, Clock, LogOut, User } from 'lucide-react';

interface AutoLogoutWarningModalProps {
  isOpen: boolean;
  timeRemaining: number;
  onStayLoggedIn: () => void;
  onLogoutNow: () => void;
}

const AutoLogoutWarningModal: React.FC<AutoLogoutWarningModalProps> = ({
  isOpen,
  timeRemaining,
  onStayLoggedIn,
  onLogoutNow
}) => {
  const [displayTime, setDisplayTime] = useState('');

  // Format time remaining for display
  useEffect(() => {
    const formatTime = (milliseconds: number) => {
      const totalSeconds = Math.ceil(milliseconds / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    setDisplayTime(formatTime(timeRemaining));
  }, [timeRemaining]);

  // Get progress percentage for the progress bar
  const getProgressPercentage = () => {
    const totalWarningTime = 2 * 60 * 1000; // 2 minutes
    return Math.max(0, Math.min(100, ((totalWarningTime - timeRemaining) / totalWarningTime) * 100));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full shadow-xl">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-8 w-8 text-amber-500" />
            </div>
            <div>
              <h3 className="text-lg font-medium leading-6 text-gray-900">
                Session Timeout Warning
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                You will be automatically logged out due to inactivity
              </p>
            </div>
          </div>

          {/* Time Remaining Display */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-center space-x-2 mb-3">
              <Clock className="h-5 w-5 text-amber-600" />
              <span className="text-2xl font-bold text-amber-800">
                {displayTime}
              </span>
            </div>
            
            {/* Progress Bar */}
            <div className="w-full bg-amber-200 rounded-full h-2">
              <div
                className="bg-amber-600 h-2 rounded-full transition-all duration-1000 ease-linear"
                style={{ width: `${getProgressPercentage()}%` }}
              />
            </div>
            
            <p className="text-sm text-amber-700 text-center mt-2">
              Time remaining before automatic logout
            </p>
          </div>

          {/* Warning Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-800">
                  Important Notice
                </h4>
                <p className="text-sm text-red-700 mt-1">
                  Any unsaved work will be lost when you are automatically logged out. 
                  Please save your work and click "Stay Logged In" to continue your session.
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3">
            <button
              type="button"
              className="flex-1 inline-flex justify-center items-center space-x-2 rounded-md border border-transparent bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              onClick={onStayLoggedIn}
            >
              <User className="h-4 w-4" />
              <span>Stay Logged In</span>
            </button>
            
            <button
              type="button"
              className="flex-1 inline-flex justify-center items-center space-x-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
              onClick={onLogoutNow}
            >
              <LogOut className="h-4 w-4" />
              <span>Logout Now</span>
            </button>
          </div>

          {/* Additional Info */}
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">
              This warning appears when you've been inactive for 18 minutes. 
              Your session will expire after 20 minutes of inactivity.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoLogoutWarningModal;
